/**
 * Shared TypeBox schema for the `x-trackframe-agent` OpenAPI annotation.
 *
 * Single source of truth for the COP-052 annotation shape — validated
 * identically by:
 *   (a) the CLI workbench validator (advisory; COP-008, COP-054)
 *   (b) the Hub spec-registration gate (authoritative 422 reject; HUB-026)
 *
 * Citations: TOOL-01, APPR-08, COP-007, COP-023, COP-052, HUB-026,
 *            D-01, D-02, D-03, D-04, D-17, D-18, D-37j.
 *
 * D-18 finalizes the previously-optional `citationFields` shape from
 * COP-052 — the `objectType` / `idField` / `labelField` / `deepLinkTemplate`
 * keys committed here are the canonical V1 shape; downstream agents should
 * treat this file as authoritative (this is the same kind of refinement that
 * HUB-026 was to COP-052).
 *
 * D-37j pins `expose` as a REQUIRED boolean at the schema layer
 * (`Type.Boolean()` with no `Type.Optional()` wrapper). The conditional
 * "if expose === true then kind + authz are required" rules continue to live
 * in `validateAgentAnnotations` (Plan 04-05) and the CLI rule file
 * (Plan 04-09) — the schema layer only pins `expose` itself.
 */
import { Type } from '@sinclair/typebox';
/**
 * Closed enum on `authz.enforced_by` per D-02. Picking a string here would
 * lose the workbench's ability to detect typos statically; the closed enum
 * also gives the Hub-side 422 a precise actionable error.
 *
 *   - `withAgentAuthz` — Next.js route handler guard (DEV-011)
 *   - `requireRelation` — Server Component guard (DEV-013)
 *   - `custom`          — track wires its own enforcement; the operation's
 *                         OpenAPI `description` MUST document the enforcement
 *                         point.
 */
export const EnforcedBySchema = Type.Union([
    Type.Literal('withAgentAuthz'),
    Type.Literal('requireRelation'),
    Type.Literal('custom'),
]);
/**
 * `kind` enum per COP-007. Read vs. write drives Phase 5's approval seam
 * interception path and Phase 4's prompt-time tool guidance.
 */
export const ToolKindSchema = Type.Union([
    Type.Literal('read'),
    Type.Literal('write'),
]);
/**
 * Closed enum on `authz.intent` per COP-149 D1. OPTIONAL — absence is legal
 * everywhere (HUB-026's 422 fires on a missing or malformed `authz` BLOCK,
 * never on a missing `intent`). An INVALID value fails this schema, so it is
 * a hub 422 and a workbench error on both surfaces by construction (COP-149
 * D2).
 *
 * `rule` and `intent` are complementary, not alternatives: `rule` is the
 * prose audit trail a reviewer reads, `intent` is the machine-checkable half
 * a validator and a DEV-012 test template can act on. Each value names a
 * test-pair SHAPE (COP-149 D3):
 *
 *   - `own-objects`       — cross-user denial pair (Bob denied, Alice allowed
 *                           on the SAME reference).
 *   - `role-states`       — role-flip pair (wrong role denied, right role
 *                           allowed, same record, same stage).
 *   - `all-track-members` — member-visible pair (member sees it, non-member
 *                           denied).
 *   - `custom`            — no mechanized shape; the `rule` prose governs and
 *                           the author writes the pair by hand.
 *
 * The set is CLOSED: adding a fifth value is a schema change on a published
 * package and a new decision (COP-149 Consequences).
 */
export const IntentSchema = Type.Union([
    Type.Literal('own-objects'),
    Type.Literal('role-states'),
    Type.Literal('all-track-members'),
    Type.Literal('custom'),
]);
/**
 * The `authz` block per D-03 + D-02. `rule` is free-form intent text bounded
 * at 200 chars — auditable in trace logs, not parsed. `enforced_by` is the
 * closed enum above. `intent` is the optional COP-149 D1 vocabulary.
 */
export const AuthzBlockSchema = Type.Object({
    rule: Type.String({ minLength: 1, maxLength: 200 }),
    enforced_by: EnforcedBySchema,
    intent: Type.Optional(IntentSchema),
});
/**
 * The `confirmation` block per COP-052 / GAP-001 Option B. `required: true`
 * triggers the Phase 5 approval seam; explicit `required: false` marks a safe
 * additive write that keeps `kind: 'write'` for audit/policy telemetry while
 * skipping the approval seam. `message` is the human-readable copy rendered
 * in the widget approval card when approval is required.
 */
export const ConfirmationSchema = Type.Object({
    required: Type.Boolean(),
    message: Type.Optional(Type.String({ maxLength: 500 })),
});
/**
 * Optional plan-card policy controlled by the track annotation and persisted
 * with each emitted plan. This is server-owned authorization for whether the
 * browser may submit `wholePlanApproved: true`; the checkbox alone is never
 * authority.
 */
export const PlanPolicySchema = Type.Object({
    allowWholePlanApproval: Type.Optional(Type.Boolean()),
    wholePlanApprovalLabel: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
}, { additionalProperties: false });
/**
 * The `citationFields` element shape per D-18 (finalization of COP-052's
 * previously-uncommitted optional field). Drives Hub-side `references`
 * mapping (TOOL-08, COP-021):
 *   - `objectType`        — citation type label (e.g. `"agreement"`)
 *   - `idField`           — JSONPath-lite (D-19) into the response body
 *   - `labelField`        — optional JSONPath-lite; falls back to
 *                           `${objectType}:${id}` when absent
 *   - `deepLinkTemplate`  — optional template like `/agreements/{id}`;
 *                           composed with `referenceBaseUrl` (D-20)
 */
export const CitationFieldSchema = Type.Object({
    objectType: Type.String({ minLength: 1, maxLength: 64 }),
    idField: Type.String({ minLength: 1, maxLength: 200 }),
    labelField: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    deepLinkTemplate: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
});
/**
 * The `redact` path grammar per APPR-08 / D-17.
 *
 * Each entry is a JSONPath-lite path with the SAME grammar enforced by
 * `agent-annotation-paths.resolveDotPath` (D-19): dotted identifier segments
 * with an optional `[]` array-iterator suffix per segment. No wildcards
 * (`*`), no recursive descent (`..`), no filter predicates (`[?...]`), no
 * bracket-indexed access (`a[0]`).
 *
 * Examples that match:
 *   - `body.ssn`
 *   - `query.token`
 *   - `body.items[].api_key`
 *   - `body.user.profile.email`
 *
 * Examples that DO NOT match (rejected by the pattern):
 *   - `body.*.api_key`        — wildcard
 *   - `body..secret`          — recursive descent
 *   - `body[0].api_key`       — bracket-indexed access
 *   - `body.items[].`         — trailing dot
 *
 * The regex enforces:
 *   - Leading segment is an identifier (`[A-Za-z_][A-Za-z0-9_]*`).
 *   - Each subsequent segment is either `[]` (array iterator) or
 *     `.identifier`.
 *   - No empty segments, no whitespace, no special characters.
 *
 * Prototype-pollution segments (`__proto__`, `constructor`, `prototype`)
 * are NOT pattern-rejected at the schema layer — the pattern would have to
 * encode a deny-list which adds drift risk. Instead, the deny-list is
 * enforced at two runtime sites that consume this schema:
 *   1. The Hub spec-registration gate (HUB-026 future extension, Plan 05-04)
 *      uses `resolveDotPath` to dry-parse each entry; `resolveDotPath`
 *      returns `[]` for forbidden segments and the gate emits
 *      `AGENT_ANNOTATION_MESSAGES.REDACT_PATH_INVALID` violations.
 *   2. The redaction runtime (`packages/hub/agent/traces/redact.ts`,
 *      Plan 05-09 consumer; see also `redactByPaths`) imports the same
 *      `FORBIDDEN_PATH_SEGMENTS` deny-list from `agent-annotation-paths`
 *      and silently no-ops with an `onSkip` callback when a forbidden
 *      segment is encountered (defense-in-depth — fail-closed at the
 *      schema gate makes this a belt-and-braces second check).
 *
 * Citations: APPR-08, COP-023, D-17, D-19.
 */
export const RedactPathSchema = Type.String({
    minLength: 1,
    maxLength: 200,
    pattern: '^[A-Za-z_][A-Za-z0-9_]*(\\[\\]|\\.[A-Za-z_][A-Za-z0-9_]*)*$',
});
/**
 * The full `x-trackframe-agent` OpenAPI annotation shape.
 *
 * `expose` is REQUIRED at the schema layer per D-37j (Cursor LOW replan-fix —
 * Plan 01 wording mismatch reconciled to schema). The conditional rules from
 * D-03 / GAP-001 Option B (`expose=true` → `kind` + `authz` required;
 * `kind: 'write'` → explicit boolean `confirmation.required` required) live in `validateAgentAnnotations`
 * (Plan 05) and `agent-annotation-rules.js` (Plan 09); both consume this
 * schema for structural validation and then layer the conditional rules on
 * top — single source for the shape, two enforcement points.
 *
 *   - `expose`           REQUIRED — Type.Boolean(), NOT Type.Optional()
 *   - `kind`             optional at schema layer; conditional in Plan 05
 *   - `toolName`         optional; defaults to operationId; regex bounded
 *   - `toolDescription`  optional; defaults to summary || description
 *   - `authz`            optional at schema layer; conditional in Plan 05
 *   - `confirmation`     optional at schema layer; conditional in Plan 05
 *   - `citationFields`   optional; capped at 16 elements per D-03 sizing
 */
export const AgentAnnotationSchema = Type.Object({
    expose: Type.Boolean(),
    kind: Type.Optional(ToolKindSchema),
    toolName: Type.Optional(Type.String({ pattern: '^[a-zA-Z][a-zA-Z0-9_]*$', maxLength: 64 })),
    toolDescription: Type.Optional(Type.String({ minLength: 1, maxLength: 1000 })),
    authz: Type.Optional(AuthzBlockSchema),
    confirmation: Type.Optional(ConfirmationSchema),
    plan: Type.Optional(PlanPolicySchema),
    citationFields: Type.Optional(Type.Array(CitationFieldSchema, { maxItems: 16 })),
    /**
     * APPR-08 / D-17: opt-in per-tool path list; redaction is applied to
     * OTel + chat-message metadata only; `tf_approval.payload` stays whole
     * (the seam must replay exact args on approve). Max 32 paths; each
     * ≤ 200 chars. Prototype-pollution segments (`__proto__`, `constructor`,
     * `prototype`) are rejected at runtime by the Hub gate (Plan 05-04 path
     * validator) AND by `resolveDotPath` at redaction time
     * (defense-in-depth — single source of truth in
     * `agent-annotation-paths.FORBIDDEN_PATH_SEGMENTS`).
     *
     * Default is no redaction (matches COP-023's deployment-policy default;
     * per-tool `redact` is opt-in by the track maintainer). Plan 05-09 wires
     * consumption at trace-emit + chat-message persist time.
     */
    redact: Type.Optional(Type.Array(RedactPathSchema, { maxItems: 32 })),
});
/**
 * The Hub 422 violation entry shape per D-04. Multiple violations are
 * surfaced as `violations: AgentAnnotationViolation[]` on a single 422
 * response. The CLI workbench mirrors this shape per D-31.
 */
export const AgentAnnotationViolationSchema = Type.Object({
    operationPath: Type.String(),
    field: Type.String(),
    message: Type.String(),
});
//# sourceMappingURL=agent-annotation.js.map